import { fabric } from "fabric";
import { Canvas as FabricCanvas } from "fabric";
import { v1 as uuid } from "uuid";
import Hammer from "hammerjs";
import { init, belongsToRect } from "@/helpers/customizerHelper";
import { PrintArea } from "@/models";
import { TextConfiguration } from "@/models/types";
import { COLOR_WHEEL_URL, WATERMARK_URL } from "@/constants";
const DISABLE_GUIDELINES_THRESHOLD = 0.15;

export enum Tags {
  LoadEmoji = "LoadEmoji"
}

type FabricEvent = {
  object: any;
  value: boolean;
  objectCenter: Position;
  eventCenter: Position;
};
type CanvasChangedListener = {
  updated: (object: any) => void;
  added: (object: any) => void;
  removed: (object: any) => void;
};

type BooleanListener = (flag: boolean) => void;
type OnTapListener = (e: any) => void;
type TrashCanListener = { onTrash: (flag: boolean, object: any) => void };
type GuidelinesListener = {
  horizontal: (show: boolean) => void;
  vertical: (show: boolean) => void;
};
type Canvas = typeof FabricCanvas;
type Position = { x: number; y: number };
export default class CanvasManager {
  private width = 0;
  private height = 0;
  canvas: Canvas = null;
  private _hammer = null;
  private _addedHandler: any = null;
  private _updatedHandler: any = null;
  private _removedHandler: any = null;
  private _trashCanListener: TrashCanListener = null;
  private _guidelinesListener: GuidelinesListener = null;
  private _canvasChangedListener: CanvasChangedListener = null;
  private _onPinchListener: BooleanListener = null;
  private _onDragListener: BooleanListener = null;
  private _onTapListener: OnTapListener = null;
  private _pinching = false;
  private _dragging = false;
  private _showHorizontalGuideline = false;
  private _showVerticalGuideline = false;
  private _trashCanPosition = null;
  private _isMobile = false;
  //{updated: (a) => {}, added: (a) => {}, removed: (a) => {}}

  constructor(id, width: number, height: number) {
    this.width = width;
    this.height = height;
    this.createCanvas(id);
  }

  //Init stuff

  init(id: string, listener) {
    try {
      init(this.canvas, this._hammer, {
        onDrag: (fe: FabricEvent) => this.handleDrag(fe),
        onPinch: (fe: FabricEvent) => this.handlePinch(fe),
      });

      listener?.complete();
    } catch (e: any) {
      listener?.error();
    }
  }

  createCanvas(id: string) {
    this.canvas = new fabric.Canvas(id, {
      preserveObjectStacking: true,
      centeredScaling: true,
      selection: false,
      stateful: true,
    });
    this.createHammerInstance();
    return this.canvas;
  }

  //import stuff

  importJSON(output: any, showControls: boolean): Promise<any> {
    return new Promise((resolve, reject) => {
      try {
        this.canvas.loadFromJSON(output || [], () => {
          this.canvas.getObjects().forEach((o: any) => {
            o.set({ hasControls: showControls });
          });
          resolve(true);
        });
      } catch (e) {
        reject(e);
      }
    });
  }

  //export stuff

  toJSON() {
    return this.canvas.toJSON(["id", "toScale"]);
  }

  toDataURL() {
    return this.canvas.toDataURL("image/png", 1.0);
  }

  toSVG() {
    return this.canvas.toSVG();
  }

  //adding objects

  createFabricImage(img: any, withControls: boolean) {
    const scaleFactorX = this.width / img.width;
    const scaleFactorY = this.height / img.height;
    const id = uuid();
    const scaleFactor = Math.min(scaleFactorX, scaleFactorY);
    img = img.set({
      hasControls: withControls,
      id,
      left: this.width / 2,
      top: this.height / 2,
      scaleX: scaleFactor,
      scaleY: scaleFactor,
      originX: "center",
      originY: "center",
    });
    return img;
  }

  createFabricSvg(obj: any, withControls: boolean) {
    const scaleFactorX = this.width / obj.width;
    const scaleFactorY = this.height / obj.height;
    const scaleFactor = Math.min(scaleFactorX, scaleFactorY);
    obj = obj.set({
      id: uuid(),
      hasControls: withControls,
      left: this.width / 2 - obj.width / 2,
      top: this.height / 2 - obj.height / 2,
      scaleX: scaleFactor,
      scaleY: scaleFactor,
      originX: "center",
      originY: "center",
    });
    return obj;
  }

  createAndAddFabricSvg(obj: any, withControls: boolean) {
    const result = this.createFabricSvg(obj, withControls);
    this.canvas.add(result);
    this.canvas.renderAll();
    this.bringTextToFront();
    return result;
  }

  createAndAddFabricText(data: TextConfiguration, withControls: boolean) {
    const result = this.createFabricText(data, withControls);
    this.canvas.add(result);
    this.canvas.renderAll();
    this.removeAllEmptyTexts();
    this.canvas.bringToFront(result);
    return result;
  }

  createFabricText(data: TextConfiguration, withControls: boolean) {
    const id = uuid();
    return new fabric.Text(data.text, {
      id: id,
      fill: data.fill || "black",
      width: this.width,
      top: this.height / 2,
      left: this.width / 2,
      fontFamily: data.fontFamily || "Poppins",
      textAlign: data.textAlign || "center",
      originX: "center",
      hasControls: withControls,
      originY: "center",
    });
  }

  createAndAddFabricImage(img: any, withControls: boolean) {
    const result = this.createFabricImage(img, withControls);
    this.canvas.add(result);
    this.canvas.renderAll();
    return result;
  }

  loadImage(path: string) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = path;
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.onerror = (e) => {
        reject(e);
      };
    });
  }

  async addWatermark(printArea: PrintArea) {
    return new Promise((resolve) => {
      const url = printArea?.watermark?.url || WATERMARK_URL;
      if (!url) {
        this.canvas.getElement().getContext("2d").imageSmoothingQuality =
          "high";
        this.canvas.backgroundColor = printArea.defaultBackgroundColor;
        this.canvas.renderAll();
        resolve(false);
        console.warn("No watermark url provided! 😮");
        return;
      }

      const offsetX = printArea?.watermarkXPercentage;
      const offsetY = printArea?.watermarkYPercentage;
      const scale = printArea?.watermarkScalePercentage * this.width;
      const angle = printArea?.watermarkRotation;

      this.canvas.setOverlayImage(
        url,
        (img: any) => {
          this.canvas.getElement().getContext("2d").imageSmoothingQuality =
            "high";
          this.canvas.renderAll();
          resolve(img);
        },
        {
          top: this.height * offsetY,
          left: this.width * offsetX,
          scaleX: scale,
          scaleY: scale,
          angle: angle,
          originX: "center",
          originY: "center",
          crossOrigin: "anonymous",
        }
      );
    });
  }

  //utils

  bringTextToFront(): void {
    this.canvas.getObjects().forEach((o: any) => {
      if (o.type === "text") {
        this.canvas.bringToFront(o);
      }
    });
  }

  removeAllEmptyTexts() {
    this.canvas.getObjects().forEach((o: any) => {
      if (o.type === "text" && !o.text) {
        this.canvas.remove(o);
        this.canvas.renderAll();
      }
    });
  }

  //Listeners
  addCanvasChangedListener(listener: CanvasChangedListener) {
    this._canvasChangedListener = listener;
    this._addedHandler = this.canvas.on("object:added", (e: any) => {
      listener.added(e);
    });
    this._updatedHandler = this.canvas.on("object:modified", (e: any) => {
      listener.updated(e);
    });
    this._removedHandler = this.canvas.on("object:removed", (e: any) => {
      listener.removed(e);
    });
  }

  addTrashCanListener(listener: TrashCanListener) {
    this._trashCanListener = listener;
  }

  addOnTapListener(listener: OnTapListener) {
    this._onTapListener = listener;
    this._hammer.on("tap", (e: any) => {
      this.handleTap(e);
    });
  }

  removeOnTapListener() {
    this._onTapListener = null;
  }

  removeTrashCanListener() {
    this._trashCanListener = null;
  }

  addGuidelinesListener(listener: GuidelinesListener) {
    this._guidelinesListener = listener;
  }
  removeGuidelinesListener() {
    this._guidelinesListener = null;
  }

  removeCanvasChangedListener() {
    this.canvas.off("object:added", this._addedHandler);
    this.canvas.off("object:modified", this._updatedHandler);
    this.canvas.off("object:removed", this._removedHandler);
    this._canvasChangedListener = null;
  }

  addOnPinchListener(listener: BooleanListener) {
    this._onPinchListener = listener;
  }
  removOnPinchListener() {
    this._onPinchListener = null;
  }

  addOnDragListener(listener: BooleanListener) {
    this._onDragListener = listener;
  }
  removeOnDragListener() {
    this._onPinchListener = null;
  }

  //Handlers

  private handleGuidelines(event: FabricEvent) {
    const object = event.object;
    const objectCenter = event.objectCenter;
    const gestureCenter = event.eventCenter;
    if (!this._dragging) {
      if (this._showHorizontalGuideline) {
        object.set({ top: this.canvas.height / 2 });
        this._showHorizontalGuideline = false;
      }
      if (this._showVerticalGuideline) {
        object.set({ left: this.canvas.width / 2 });
        this._showVerticalGuideline = false;
      }
      this.canvas.renderAll();
      this._showVerticalGuideline = false;
      this._guidelinesListener.horizontal(this._showHorizontalGuideline);
      this._guidelinesListener.vertical(this._showVerticalGuideline);
      return;
    }
    const threshold = 2;
    const verticalRect = this.canvas.upperCanvasEl?.getBoundingClientRect();
    verticalRect.x = verticalRect.left + verticalRect.width / 2 - threshold;
    verticalRect.width = 2 * threshold;

    const vertical = belongsToRect(objectCenter, verticalRect, 0);
    const disableLockOnX =
      Math.abs(objectCenter.x - gestureCenter.x) / this.width >
      DISABLE_GUIDELINES_THRESHOLD;
    this._showVerticalGuideline = vertical && !disableLockOnX;
    if (disableLockOnX) {
      object.set({ lockMovementX: false });
    } else {
      object.set({
        lockMovementX: this._showVerticalGuideline,
        left: this.canvas.width / 2,
      });
    }

    const horizontalRect = this.canvas.upperCanvasEl?.getBoundingClientRect();
    horizontalRect.y =
      horizontalRect.top + horizontalRect.height / 2 - threshold;
    horizontalRect.height = 2 * threshold;
    const horizontal = belongsToRect(objectCenter, horizontalRect, 0);
    const disableLockOnY =
      Math.abs(objectCenter.y - gestureCenter.y) / this.height >
      DISABLE_GUIDELINES_THRESHOLD;
    this._showHorizontalGuideline = horizontal && !disableLockOnY;
    if (disableLockOnY) {
      object.set({ lockMovementY: false });
    } else {
      object.set({
        lockMovementY: this._showHorizontalGuideline,
        top: this.canvas.height / 2,
      });
    }
    this._guidelinesListener.horizontal(this._showHorizontalGuideline);
    this._guidelinesListener.vertical(this._showVerticalGuideline);
  }

  gentlyRemoveFromCanvas(object: any): void {
    object.animate("opacity", "0", {
      duration: 200,
      onChange: this.canvas.renderAll.bind(this.canvas),
      onComplete: () => {
        this.canvas.remove(object);
      },
    });
  }

  setIsMobile(mobile: boolean): void {
    this._isMobile = mobile;
  }

  private handleDeletion(event: FabricEvent) {
    this._trashCanPosition = document
      .getElementById("trashLayer")
      ?.getBoundingClientRect();
    if (!this._trashCanPosition) {
      return;
    }

    const inDeleteZone = belongsToRect(
      event.eventCenter,
      this._trashCanPosition,
      0
    );
    if (this._pinching) {
      return;
    }
    this._trashCanListener.onTrash(inDeleteZone, event.object);
    if (inDeleteZone && !this._dragging) {
      this.gentlyRemoveFromCanvas(event.object);
    }
  }

  private handleDrag(event: FabricEvent) {
    this._dragging = event.value;
    this._onDragListener?.(event.value);
    if (!this._pinching) {
      this.handleDeletion(event);
    }
    if (this._isMobile) {
      this.handleGuidelines(event);
    }
  }

  private handlePinch(event: FabricEvent) {
    this._pinching = event.value;
    this._onPinchListener?.(event.value);
    if (!this._pinching) {
      this.handleDeletion(event);
    }
  }

  private handleTap(event: Event) {
    this._onTapListener(event);
  }

  private createHammerInstance() {
    this._hammer = new Hammer.Manager(this.canvas.upperCanvasEl);
  }
}
